/**
 * CelebrationManager - Handles celebration animations and notifications
 *
 * Features:
 * - Confetti explosion for ATH
 * - Gold glow animation for milestones
 * - Fire streak animation
 * - Sound effects integration
 * - Queue management for multiple celebrations
 * - Enhanced desktop celebrations (full-screen, larger, more prominent)
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useVault, CelebrationType, Celebration } from '../../contexts/VaultContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useDesktopView } from '../../hooks/useDesktopView';

interface CelebrationManagerProps {
  children?: React.ReactNode;
}

const CelebrationManager: React.FC<CelebrationManagerProps> = ({ children }) => {
  const { activeCelebration, dismissCelebration, celebrationsEnabled } = useVault();
  const { playSound, celebrationsEnabled: feedbackCelebrationsEnabled } = useFeedback();
  const { isDesktop } = useDesktopView();
  const confettiRef = useRef<HTMLCanvasElement>(null);

  // ATH Confetti celebration - enhanced for desktop
  const triggerATHCelebration = useCallback(() => {
    if (!feedbackCelebrationsEnabled) return;

    playSound('celebration');

    // Fire confetti from both sides - more particles on desktop
    const duration = isDesktop ? 4000 : 3000;
    const animationEnd = Date.now() + duration;
    const baseParticles = isDesktop ? 80 : 50;
    const defaults = {
      startVelocity: isDesktop ? 45 : 30,
      spread: 360,
      ticks: isDesktop ? 80 : 60,
      zIndex: 9999
    };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: NodeJS.Timer = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = baseParticles * (timeLeft / duration);

      // Left side burst
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FFA500', '#22c55e', '#ffffff'],
      });

      // Right side burst
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FFA500', '#22c55e', '#ffffff'],
      });

      // Center burst for desktop
      if (isDesktop) {
        confetti({
          ...defaults,
          particleCount: particleCount / 2,
          origin: { x: 0.5, y: 0.3 },
          colors: ['#FFD700', '#FFA500', '#22c55e', '#ffffff'],
        });
      }
    }, 200);

    return () => clearInterval(interval);
  }, [playSound, feedbackCelebrationsEnabled, isDesktop]);

  // Milestone celebration - enhanced for desktop
  const triggerMilestoneCelebration = useCallback(() => {
    if (!feedbackCelebrationsEnabled) return;

    playSound('success');

    // Single burst of gold confetti - larger on desktop
    confetti({
      particleCount: isDesktop ? 150 : 100,
      spread: isDesktop ? 100 : 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#DAA520'],
      zIndex: 9999,
    });
  }, [playSound, feedbackCelebrationsEnabled, isDesktop]);

  // Streak celebration - enhanced for desktop
  const triggerStreakCelebration = useCallback(() => {
    if (!feedbackCelebrationsEnabled) return;

    playSound('streak');

    // Fire-themed confetti - more particles on desktop
    confetti({
      particleCount: isDesktop ? 120 : 80,
      spread: isDesktop ? 140 : 100,
      origin: { y: 0.7 },
      colors: ['#FF4500', '#FF6347', '#FFD700', '#FFA500'],
      zIndex: 9999,
    });
  }, [playSound, feedbackCelebrationsEnabled, isDesktop]);

  // Handle active celebration
  useEffect(() => {
    if (!activeCelebration) return;

    switch (activeCelebration.type) {
      case 'ath':
        triggerATHCelebration();
        break;
      case 'milestone':
        triggerMilestoneCelebration();
        break;
      case 'streak':
        triggerStreakCelebration();
        break;
    }
  }, [activeCelebration, triggerATHCelebration, triggerMilestoneCelebration, triggerStreakCelebration]);

  return (
    <>
      {children}

      {/* Celebration overlay */}
      <AnimatePresence>
        {activeCelebration && (
          <CelebrationOverlay
            celebration={activeCelebration}
            onDismiss={() => dismissCelebration(activeCelebration.id)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Celebration overlay component
interface CelebrationOverlayProps {
  celebration: Celebration;
  onDismiss: () => void;
}

const CelebrationOverlay: React.FC<CelebrationOverlayProps> = ({
  celebration,
  onDismiss,
}) => {
  // Auto-dismiss after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const getContent = () => {
    switch (celebration.type) {
      case 'ath':
        return <ATHCelebration value={celebration.data.value} onDismiss={onDismiss} />;
      case 'milestone':
        return (
          <MilestoneCelebration
            name={celebration.data.name}
            value={celebration.data.value}
            onDismiss={onDismiss}
          />
        );
      case 'streak':
        return <StreakCelebration count={celebration.data.count} onDismiss={onDismiss} />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {getContent()}
    </motion.div>
  );
};

// ATH Celebration component
interface ATHCelebrationProps {
  value?: number;
  onDismiss: () => void;
}

const ATHCelebration: React.FC<ATHCelebrationProps> = ({ value, onDismiss }) => {
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(val);
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-yellow-500/20 to-amber-600/20 backdrop-blur-lg rounded-2xl p-8 mx-4 pointer-events-auto border border-yellow-500/30"
      initial={{ scale: 0.5, y: 50, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.8, y: -50, opacity: 0 }}
      transition={{ type: 'spring', damping: 15 }}
      onClick={onDismiss}
    >
      <motion.div
        className="text-6xl text-center mb-4"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 10, -10, 0],
        }}
        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
      >
        🏆
      </motion.div>

      <motion.h2
        className="text-3xl font-bold text-center text-yellow-400 mb-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        New All-Time High!
      </motion.h2>

      {value && (
        <motion.p
          className="text-2xl font-semibold text-center text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {formatCurrency(value)}
        </motion.p>
      )}

      <motion.p
        className="text-sm text-center text-gray-400 mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Tap to dismiss
      </motion.p>
    </motion.div>
  );
};

// Milestone Celebration component
interface MilestoneCelebrationProps {
  name?: string;
  value?: number;
  onDismiss: () => void;
}

const MilestoneCelebration: React.FC<MilestoneCelebrationProps> = ({
  name,
  value,
  onDismiss,
}) => {
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(val);
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-purple-500/20 to-indigo-600/20 backdrop-blur-lg rounded-2xl p-8 mx-4 pointer-events-auto border border-purple-500/30"
      initial={{ scale: 0.5, y: 50, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.8, y: -50, opacity: 0 }}
      transition={{ type: 'spring', damping: 15 }}
      onClick={onDismiss}
    >
      {/* Glowing badge effect */}
      <motion.div
        className="relative w-24 h-24 mx-auto mb-4"
        animate={{
          boxShadow: [
            '0 0 20px rgba(168, 85, 247, 0.5)',
            '0 0 40px rgba(168, 85, 247, 0.8)',
            '0 0 20px rgba(168, 85, 247, 0.5)',
          ],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-indigo-600 rounded-full flex items-center justify-center">
          <span className="text-4xl">🎯</span>
        </div>
      </motion.div>

      <motion.h2
        className="text-2xl font-bold text-center text-purple-400 mb-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        Milestone Achieved!
      </motion.h2>

      {name && (
        <motion.p
          className="text-xl font-semibold text-center text-white mb-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {name}
        </motion.p>
      )}

      {value && (
        <motion.p
          className="text-lg text-center text-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {formatCurrency(value)}
        </motion.p>
      )}

      <motion.p
        className="text-sm text-center text-gray-400 mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Tap to dismiss
      </motion.p>
    </motion.div>
  );
};

// Streak Celebration component
interface StreakCelebrationProps {
  count?: number;
  onDismiss: () => void;
}

const StreakCelebration: React.FC<StreakCelebrationProps> = ({ count, onDismiss }) => {
  // Generate fire emojis based on streak count
  const fireEmojis = Array.from({ length: Math.min(count || 0, 10) }, (_, i) => i);

  return (
    <motion.div
      className="bg-gradient-to-br from-orange-500/20 to-red-600/20 backdrop-blur-lg rounded-2xl p-8 mx-4 pointer-events-auto border border-orange-500/30"
      initial={{ scale: 0.5, y: 50, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.8, y: -50, opacity: 0 }}
      transition={{ type: 'spring', damping: 15 }}
      onClick={onDismiss}
    >
      {/* Animated fire emojis */}
      <div className="flex justify-center mb-4">
        {fireEmojis.map((_, index) => (
          <motion.span
            key={index}
            className="text-4xl"
            initial={{ y: 20, opacity: 0, scale: 0 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{
              delay: index * 0.1,
              type: 'spring',
              damping: 10,
            }}
          >
            🔥
          </motion.span>
        ))}
      </div>

      <motion.h2
        className="text-2xl font-bold text-center text-orange-400 mb-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {count} Day Winning Streak!
      </motion.h2>

      <motion.p
        className="text-lg text-center text-gray-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        You're on fire! Keep it going!
      </motion.p>

      <motion.p
        className="text-sm text-center text-gray-400 mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Tap to dismiss
      </motion.p>
    </motion.div>
  );
};

export default CelebrationManager;
